import React, { FC, memo, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { getInitialProps, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { doLogout, setCurrentCampus, setIsDarkMode } from '../../store/auth.slice';
import { doRemoveAuth } from '../../store/auth.slice';
import { RootState } from '../../store/rootReducer';
import { useSnackbar } from 'notistack';
import SnackbarAction from '../SnackbarAction';
import * as cookie from '../../lib/cookie';
import ConfirmModal from '../modals/ConfirmModal';
import useAxios from '../../hooks/useAxios';
import { getUnreadMessages } from '../../api/messages.api';
import { IGetUnreadMessages } from '../../models/messages.model';
import { useHistory } from 'react-router-dom';
import { IGetNotices } from '../../models/boardNotice.model';
import moment from 'moment';
import { Select } from 'antd';
import Breadcrumb from './Breadcrumb';
import SelectCampusModal from './modals/SelectCampusModal';

import { IGetHeaderAlarmInquiries } from '../../models/headerAlarm.model';
import { getHeaderAlarmNotices, getHeaderAlarmInquiries } from '../../api/alarm.api';
import AlarmPopup from '../AlarmPopup';
import MessagePopup from '../MessagePopup';
import { IGetExpenseAlarmList } from '../../models/financials.model';
import { getExpenseAlarmList } from '../../api/financials.api';

const Navbar: FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { auth, isDarkMode, currentCampus, isLogin } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const DEFAULT_IMAGE = '/images/header_profile.png';

  const { data: res, execute } = useAxios<IGetNotices>();
  const { data: res2, execute: execute2 } = useAxios<IGetUnreadMessages>();
  const { data: res4, error: error4, execute: execute4 } = useAxios<IGetHeaderAlarmInquiries>();
  const { data: res5, execute: execute5} = useAxios<IGetExpenseAlarmList>();

  const [alarmList, setAlarmList] = useState<any>([]);
  const [alarmGroup, setAlarmGroup] = useState<any>();
  const [confirmLogoutVisible, setConfirmLogoutVisible] = useState(false);
  const [selectCampusVisible, setSelectCampusVisible] = useState(false);
  const [isNoticeAlarmVisible, toggleNotice] = useReducer((state) => !state, false);
  const [isMessageAlarmVisible, toggleMessage] = useReducer((state) => !state, false);
  const ALARM_RECENT_VIEW_DAY = 3;
  const _messageData = res2?.data
    .map(message => {
      return {
        id: message.messageCode,
        type: '메세지',
        title: message.title,
        date: message.sendDate,
        link: `/message/receive`
      }
    })
    .reduce((acc: any, curr) => {
      const { date } = curr;
      const _date = moment(date).format('YYYY-MM-DD');
      if (acc[_date]) acc[_date].push(curr);
      else acc[_date] = [curr];
      return acc;
    }, {})

  const _noticeData = res?.data
    .map(notice => {
      return {
        id: notice.noticeCode,
        type: '공지',
        title: notice.title,
        date: notice.regDate,
        link: `/board/notice`
      }
    }).reduce((acc: any, curr) => {
      const { date } = curr;
      const _date = moment(date).format('YYYY-MM-DD');
      if (acc[_date]) acc[_date].push(curr);
      else acc[_date] = [curr];
      return acc;
    }, {})

  useEffect(() => {
    if (isLogin) return;

    onLogout();
  }, [isLogin]);

  useEffect(() => {
    execute(getHeaderAlarmNotices({
      pageNo: 1,
      perPage: 999999999,
      srtDate: moment().subtract(ALARM_RECENT_VIEW_DAY, 'day').format('YYYYMMDD'),
      endDate: moment().add(1, 'days').format('YYYYMMDD'),
      memberCode: auth.memberCode
    }));
    execute2(getUnreadMessages());
  }, []);

  useEffect(() => {
    execute4(getHeaderAlarmInquiries());
    execute5(getExpenseAlarmList());
  }, []);

  const onChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    cookie.set('language', lang, 24 * 365);
  }
  

  const action = (id: string | number) => <SnackbarAction id={id} />;

  const onLogout = async () => {
    setConfirmLogoutVisible(false);
    const res = await dispatch(doLogout());

    switch (res?.status) {
      case 200:
      case 401:
        history.push('/');
        dispatch(doRemoveAuth());
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), {
          variant: 'error',
          persist: true,
          action
        });
    }
  }

  const getCampusName = useCallback((campusCode: string) => {
    if (!campusCode) return 'NO CAMPUS';

    const campus = campusData.find(item => item.campusCode === campusCode);
    return (
      i18n.language === 'ko' ? campus?.campusKorName : campus?.campusEngName
    );
  }, [currentCampus, campusData])


  useEffect(()=> {
    const alarmType = 'notice';
    if(res && res.data.length > 0) {
      const result = [
        ...alarmList.filter((v: any)=> v.alarmType !== alarmType), 
        ...res.data.map(v=> ({...v, alarmType}))
      ];
      setAlarmList(result);
    }
    
  }, [res]);

  useEffect(()=> {
    const alarmType = 'inquiry';
    if(res4 && res4.data.length > 0) {

      const result = res4.data
        .filter((data: any)=> {
          const today = new Date();
          const targetDay = today.getDay() - ALARM_RECENT_VIEW_DAY;
          const recentDay = new Date( today.setDate(targetDay) );

          return new Date(data.regDate) >= recentDay;
        })
        .sort((a, b)=> Number(b.qnaCode) - Number(a.qnaCode))
        .map((v)=> ({...v, alarmType}));

      const arr = [...alarmList.filter((v: any)=> v.alarmType !== alarmType), ...result];
      setAlarmList(arr);
    }
   
  }, [res4]);

  useEffect(()=> {
    const alarmType = 'expense';
    if(res5 && res5.data.length > 0) {
      const result: any[] = [];
      res5.data.forEach((item)=>{
        let alarmSubTypeName = '';
        if(item.alarmType === 'PE') {
          alarmSubTypeName = t('campus.shuttle.button1');
        } else if(item.alarmType === 'RJ') {
          alarmSubTypeName = t('payment.expense.btn5');
        } else if(item.alarmType === 'CM') {
          alarmSubTypeName = t('payment.expense.label9');
        }

        result.push({alarmType:alarmType, subAlarmType:item.alarmType, regDate:moment().format('YYYY-MM-DD'), title:t('payment.expense.label8') + ' '+ alarmSubTypeName + ' (' + item.alarmCount + ') ' + item.campusName});
        
      });
      const arr = [...alarmList, ...result];
      setAlarmList(arr);
    }
  }, [res5]);

  useEffect(()=> {
    const result = alarmList
      .sort((a: any, b: any) => new Date(b.regDate).getTime() - new Date(a.regDate).getTime())
      .reduce((acc: any, curr: any)=> {
        const date = curr.regDate.substring(0, 10);
        return acc[date] 
          ? { ...acc, [date]: [...acc[date], curr] } 
          : { ...acc, [date]: [curr] };
      }, {})
     
    setAlarmGroup(result);
  }, [alarmList]);
  


  return (
    <>
      <nav className="common-header">
        <Breadcrumb />
        <div className="menu-right">
          <ul>
            {
              process.env.REACT_APP_IS_DEV === 'Y' ? (
                <li>
                  <b>[{t('common.layout.2')}]</b>
                </li>
              ) : undefined
            }
            <li onClick={() => {
              if (auth.hqYn && (auth.hqYn === 'Y' || auth.campus.length > 1)) {
                setSelectCampusVisible(true)
              }
            }}>
              <a href="#">
                <img src={auth.profileImage ? auth.profileImage?.thumbnailLocation : DEFAULT_IMAGE}
                     className="profile"
                     alt={"프로필 이미지"}
                     onError={(e) => {
                       e.currentTarget.src = DEFAULT_IMAGE;
                     }}

                />
                <span className="campus">{getCampusName(currentCampus)}</span>
                <b>{auth ? auth.korName : ''}</b>

              </a>
            </li>
            <li className="dropdown" onClick={toggleNotice}>
              <a href="#">
                <span className={`ic_bell ${alarmList.length > 0 ? 'active' : ''}`}></span>
              </a>
              {/* <AlarmPopup isVisible={isNoticeAlarmVisible} data={_noticeData} /> */}
              <AlarmPopup 
                data={alarmGroup}
                execute={execute}
                execute4={execute4}
                isVisible={isNoticeAlarmVisible}
              />
            </li>
            <li className="dropdown" onClick={toggleMessage}>
              <a href="#">
                <span className={`ic_chat ${(_messageData && Object.keys(_messageData).length > 0) ? 'active' : ''}`}></span>
              </a>
              <MessagePopup isVisible={isMessageAlarmVisible} data={_messageData} />
            </li>
            <li onClick={() => setConfirmLogoutVisible(true)}>
              <a href="#">
                <span className="btn-logout"></span>
              </a>
            </li>
            <li>
              <Select
                value={i18n.language}
                onChange={onChangeLanguage}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'ko', label: 'Korean' },
                ]}
              />
            </li>
          </ul>
        </div>
      </nav>

      {selectCampusVisible && (
        <SelectCampusModal
          visible={selectCampusVisible}
          setVisible={setSelectCampusVisible}
        />
      )}

      {confirmLogoutVisible &&
        <ConfirmModal
          visible={confirmLogoutVisible}
          setVisible={setConfirmLogoutVisible}
          onConfirm={onLogout}
          titleText={t('common.layout.9')}
          messageText={t('common.layout.10')}
          confirmText={t('common.button.confirm')}
          cancelText={t('common.button.cancel')}
          className="text-center"
        />
      }
    </>
  )
}

export default memo(Navbar);
